'use client';
import React, { Suspense, useEffect, useState } from 'react';
import { Row, Col, ListGroup, Container } from 'react-bootstrap';
import { Facebook, Instagram, Pintrest, Search, Twitter } from '../Icons';
import Button from '../button/button';
import './footer.scss';
import { Logo } from '../Icons/logo';
import ToggleMenu from '../toggleMenu/toggleMenu';
import { SearchInputWithButton } from '../searchInputWithButton/searchInputWithButton';
import Link from 'next/link';

interface FooterColumn {
  title: string;
  links?: { label: string; href: string }[];
  buttons?: { label: string; variant: string; href: string }[];
  search?: { placeholder: string; buttonLabel: React.ReactNode };
  socialIcons?: { icon: React.ReactNode; href: string }[];
}

const footerData: FooterColumn[] = [
  {
    title: 'Use It For',
    links: [
      {
        label: 'School Sign Ups',
        href: `/how-to-use/schools`,
      },
      {
        label: 'Business Schedules',
        href: `/how-to-use/businesses`,
      },
      {
        label: 'Volunteer Sign Ups',
        href: `/how-to-use/nonprofit-volunteer-management`,
      },
      {
        label: 'Potluck Invites',
        href: `/how-to-use/potluck`,
      },
      {
        label: 'Fundraising',
        href: `/how-to-use/collect-money`,
      },
      {
        label: 'Sports Events',
        href: `/how-to-use/sportsteams`,
      },
      {
        label: 'Church & Temple',
        href: `/how-to-use/churches`,
      },
      {
        label: 'College Activities',
        href: `/how-to-use/colleges`,
      },
      {
        label: 'Scouting Events',
        href: `/how-to-use/scouts`,
      },
      {
        label: 'School Spirit Wear',
        href: `/how-to-use/school-spirit-wear-sales-software`,
      },
      {
        label: 'Online Auctions',
        href: 'https://auctions.signupgenius.com/?utm_source=signupgenius.com&utm_medium=referral&utm_campaign=footer',
      },
      {
        label: 'Donations Campaigns',
        href: 'https://donations.signupgenius.com/?utm_source=signupgenius.com&utm_medium=referral&utm_campaign=footer',
      },
      {
        label: 'Ticket Management',
        href: 'https://tickets.signupgenius.com/#/welcome?utm_source=signupgenius.com&utm_medium=referral&utm_campaign=footer',
      },
    ],
  },
  {
    title: 'Helpful For',
    links: [
      {
        label: 'Event Registrations',
        href: `/how-to-use/event-registration-software`,
      },
      {
        label: 'Lessons & Camp Sign Ups',
        href: `/how-to-use/lessons-tutoring-registration-software`,
      },
      {
        label: 'Parent Teacher Conferences',
        href: `/how-to-use/parent-teacher-conferences`,
      },
      {
        label: 'Meal Sign Ups',
        href: `/how-to-use/online-meal-volunteer-sheets`,
      },
      {
        label: 'School Fundraising',
        href: `/how-to-use/school-payments`,
      },
      {
        label: 'Festivals & Carnivals',
        href: `/how-to-use/event-registration-software`,
      },
      {
        label: 'Book Fair Volunteers',
        href: `/how-to-use/bookfair`,
      },
      {
        label: 'Dance Class Registrations',
        href: `/how-to-use/dance-studio-registration-software`,
      },
      {
        label: 'Direct Sales Businesses',
        href: `/how-to-use/collect-money`,
      },
      {
        label: 'Photography Sessions',
        href: `/how-to-use/photo-session-registration-software`,
      },
    ],
  },
  {
    title: 'About SignUpGenius',
    links: [
      {
        label: 'Getting Started with Sign Ups',
        href: `/how-to-use/starterkit`,
      },
      {
        label: 'Features',
        href: `/how-to-use/features`,
      },
      { label: 'Pricing', href: `/pricing` },
      {
        label: 'Enterprise',
        href: `/enterprise`,
      },
      {
        label: 'Collect Money',
        href: `/how-to-use/collect-money`,
      },
      {
        label: 'Reviews',
        href: `/case-studies`,
      },
      { label: 'About Us', href: `/about` },
      {
        label: 'Press Releases',
        href: `/news`,
      },
      {
        label: 'Press Kit',
        href: `/press-kit`,
      },
      { label: 'Careers', href: `/careers` },
      {
        label: 'Advertise with Us',
        href: `/advertisewithus`,
      },
    ],
  },
  {
    title: 'Resources & Tips',
    links: [
      {
        label: 'Sign Up Designs',
        href: `/themes`,
      },
      {
        label: 'Planning Ideas',
        href: `/resources`,
      },
      { label: 'Blog', href: `/blog` },
      {
        label: 'Case Studies',
        href: `/case-studies`,
      },
      { label: 'Videos', href: `/videos` },
      {
        label: 'Printables',
        href: `/printables`,
      },
      {
        label: 'Giving Back',
        href: `/how-to-use/giving-back`,
      },
      {
        label: 'Partner Integrations',
        href: `/integrations#/`,
      },
      { label: 'API Docs', href: 'https://developer.signupgenius.com/' },
      { label: 'Sitemap', href: `/sitemap` },
      { label: 'Help', href: `/support` },
      { label: 'Contact Us', href: `/help` },
    ],
  },
  {
    title: 'Get Started',
    buttons: [
      {
        label: 'Create a Sign Up',
        variant: 'primary',
        href: `/index.cfm?go=w.Welcome&prior=w.createSignUp&formName=registerForm`,
      },
      {
        label: 'Find a Sign Up',
        variant: 'primary',
        href: `/findasignup`,
      },
    ],
    search: { placeholder: 'Search Resources', buttonLabel: <Search /> },
    socialIcons: [
      { icon: <Pintrest />, href: 'https://pinterest.com/signupgenius' },
      { icon: <Facebook />, href: 'https://www.facebook.com/signupgenius' },
      { icon: <Twitter />, href: 'https://twitter.com/signupgenius' },
      { icon: <Instagram />, href: 'https://instagram.com/signupgenius' },
    ],
  },
];

export default function Footer() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggleCollapse = () => {
    if (window.innerWidth < 767) {
      setIsCollapsed((prev) => !prev);
    }
  };
  const clickOnLinkHandle = () => {
    if (window.innerWidth < 767) {
      setIsCollapsed(true);
    }
  };
  useEffect(() => {
    const checkScreenSize = () => {
      setIsCollapsed(window.innerWidth < 767);
    };
    checkScreenSize();
    // window.addEventListener('resize', checkScreenSize);
    // return () => {
    //   window.removeEventListener('resize', checkScreenSize);
    // };
  }, []);
  return (
    <footer className="footerStyled">
      <div className="footer text-black">
        <Container>
          <div className="footer-container">
            <Row>
              <div className="mobile-footer d-block d-md-none">
                <Row>
                  <Col>
                    <div className="d-flex justify-content-center">
                      <ToggleMenu
                        className={`footer-toggle ${
                          isCollapsed ? 'collapsed' : ''
                        }`}
                        onClick={toggleCollapse}
                        aria-expanded={!isCollapsed}
                        aria-controls="collapseFooter"
                        aria-label="Toggle footer menu"
                      />
                    </div>
                  </Col>
                </Row>
              </div>
              {footerData.slice(0, 4).map((column, index) => (
                <Col
                  key={index}
                  xs={6}
                  md
                  id="collapseFooter"
                  className={`footer-collapse-content ${
                    isCollapsed ? 'd-none' : ''
                  }`}
                >
                  <h2 className="d-flex">
                    {column.title === 'About SignUpGenius' ? (
                      <>
                        About <span className="d-block d-lg-none mx-1">Us</span>
                        <span className="d-none d-lg-block mx-1">
                          SignUpGenius
                        </span>
                      </>
                    ) : (
                      column.title
                    )}
                  </h2>
                  {column.links && (
                    <ListGroup variant="flush">
                      {column.links.map((link, idx) => (
                        <ListGroup.Item
                          key={idx}
                          className="border-0 p-0"
                          onClick={clickOnLinkHandle}
                        >
                          <Link
                            href={link.href}
                            className="text-decoration-none"
                            aria-label={link.label}
                            scroll={true}
                          >
                            {link.label}
                          </Link>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </Col>
              ))}
              <Col xl lg={12}>
                <div className="form-column">
                  <h2>{footerData[4].title}</h2>
                  {/* Buttons */}
                  <div className="d-grid gap-2">
                    {footerData[4].buttons &&
                      footerData[4].buttons.map((button, idx) => (
                        <Button key={idx} variant="primary" href={button.href}>
                          {button.label}
                        </Button>
                      ))}
                  </div>
                  {/* Search Input */}
                  <Suspense>
                    <SearchInputWithButton
                      placeholder={footerData[4]?.search?.placeholder ?? ''}
                      buttonLabel={footerData[4]?.search?.buttonLabel ?? ''}
                      onButtonClick={() => console.log('Button clicked!')}
                      ariaLabel="Search Resources"
                    />
                  </Suspense>
                  {/* Social Media Icons */}
                  <div className="d-flex justify-content-center social-icons--content">
                    {footerData[4].socialIcons &&
                      footerData[4].socialIcons.map((icon, idx) => (
                        <Button
                          key={idx}
                          variant="link"
                          href={icon.href}
                          className="social-icon mx-1 p-0"
                        >
                          {icon.icon}
                        </Button>
                      ))}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
      <div className="sub-footer">
        <Container>
          <Row className="align-items-center">
            <Col>
              <div className="d-flex align-items-center gap-3 subfooter-content">
                <div className="logo">
                  <Link href="/" aria-label="Go to homepage" scroll={true}>
                    <Logo size={26} />
                  </Link>
                </div>
                {/* <div className="mobile lumaverselogo d-block d-lg-none mx-2">
                  <Link href="/" aria-label="Go to Lumaverse website">
                    <Lumaverselogo />
                  </Link>
                </div> */}
                <div className="text-content d-flex">
                  <div className="copyright-text">
                    <p>©2024 SignUpGenius, Inc. All Rights Reserved. </p>
                  </div>
                  <div className="d-flex links gap-3">
                    <Link
                      href={`/privacy`}
                      aria-label="Privacy Policy"
                      target="_blank"
                      scroll={true}
                    >
                      Privacy Policy
                    </Link>
                    <Link
                      href={`/terms-of-service`}
                      aria-label="Terms of Service"
                      target="_blank"
                      scroll={true}
                    >
                      Terms of Service
                    </Link>
                    <a
                      className="change-consent pmLink"
                      aria-label="Do Not Sell My Information"
                      style={{ visibility: 'hidden', cursor: 'pointer' }}
                    >
                      &nbsp;
                    </a>
                    <a
                      className="change-consent gdpr-pmLink"
                      aria-label="Privacy Preferences"
                      style={{ visibility: 'hidden', cursor: 'pointer' }}
                    >
                      &nbsp;
                    </a>
                  </div>
                </div>
              </div>
            </Col>
            {/* <Col md="auto">
              <div className="lumaverselogo d-none d-lg-block">
                <Link href="#" aria-label="Go to Lumaverse website">
                  <Lumaverselogo />
                </Link>
              </div>
            </Col> */}
          </Row>
        </Container>
      </div>
    </footer>
  );
}
